import { validarCenaTabletop } from '../serializacao'
import type { CenaTabletop } from '../tipos'
import { ESTADO_VITIMA_VAZIO } from './efeitosClinicos'
import { criarSessaoAtendimento } from './operacoesAtendimento'
import { normalizarFaseAtendimento } from './tipos'
import type { EstadoVitimaNaSessao, SessaoAtendimentoTabletop } from './tipos'

/**
 * Sessão de atendimento salva no navegador, para retomar a aula depois de
 * recarregar a página. Guarda as duas cenas — a cópia da sessão (cortes,
 * vidros, calços) e o cenário preparado (para "Reiniciar sessão" continuar
 * funcionando) — e o atendimento inteiro, que já é JSON puro.
 */
export interface SessaoSalva {
  readonly versao: 1
  /** instante real em que foi salva (ms desde 1970), para o aviso "salva às 14:05" */
  readonly salvoEm: number
  readonly cenaSessao: CenaTabletop
  readonly cenaPreparada: CenaTabletop
  readonly sessao: SessaoAtendimentoTabletop
}

export function serializarSessaoSalva(dados: SessaoSalva): string {
  return JSON.stringify(dados)
}

const ehRegistro = (valor: unknown): valor is Record<string, unknown> =>
  typeof valor === 'object' && valor !== null && !Array.isArray(valor)

/**
 * Completa o que faltar com os padrões de uma sessão nova: uma sessão salva
 * por uma versão anterior do app (sem `operacoes`, sem `riscos`...) continua
 * abrindo. O cronômetro volta sempre pausado — o instrutor retoma quando a
 * turma estiver pronta.
 */
function normalizarSessao(valor: Record<string, unknown>): SessaoAtendimentoTabletop | null {
  if (typeof valor.id !== 'string' || typeof valor.tempoSimuladoSegundos !== 'number') return null
  const padrao = criarSessaoAtendimento()
  const estados = ehRegistro(valor.estadosVitimas) ? valor.estadosVitimas : {}
  const estadosVitimas: Record<string, EstadoVitimaNaSessao> = {}
  for (const [id, estado] of Object.entries(estados)) {
    if (ehRegistro(estado)) estadosVitimas[id] = { ...ESTADO_VITIMA_VAZIO, ...(estado as Partial<EstadoVitimaNaSessao>) }
  }
  const lista = <T,>(campo: unknown, reserva: readonly T[]): readonly T[] => (Array.isArray(campo) ? (campo as T[]) : reserva)
  return {
    ...padrao,
    ...(valor as Partial<SessaoAtendimentoTabletop>),
    fase: normalizarFaseAtendimento(valor.fase),
    avaliacaoCena: ehRegistro(valor.avaliacaoCena)
      ? { ...padrao.avaliacaoCena!, ...(valor.avaliacaoCena as Partial<NonNullable<SessaoAtendimentoTabletop['avaliacaoCena']>>) }
      : padrao.avaliacaoCena,
    estadosVitimas,
    vitimasIdentificadas: lista(valor.vitimasIdentificadas, padrao.vitimasIdentificadas),
    marcos: lista(valor.marcos, padrao.marcos),
    operacoes: lista(valor.operacoes, padrao.operacoes),
    historicoEventos: lista(valor.historicoEventos, padrao.historicoEventos),
    riscos: ehRegistro(valor.riscos) ? (valor.riscos as SessaoAtendimentoTabletop['riscos']) : padrao.riscos,
    cronometroRodando: false,
  }
}

/** `null` quando o conteúdo não é uma sessão salva utilizável (JSON inválido, versão desconhecida, cena inválida). */
export function desserializarSessaoSalva(json: string): SessaoSalva | null {
  let valor: unknown
  try {
    valor = JSON.parse(json)
  } catch {
    return null
  }
  if (!ehRegistro(valor) || valor.versao !== 1 || typeof valor.salvoEm !== 'number') return null
  if (!validarCenaTabletop(valor.cenaSessao) || !validarCenaTabletop(valor.cenaPreparada)) return null
  if (!ehRegistro(valor.sessao)) return null
  const sessao = normalizarSessao(valor.sessao)
  if (!sessao) return null
  return { versao: 1, salvoEm: valor.salvoEm, cenaSessao: valor.cenaSessao, cenaPreparada: valor.cenaPreparada, sessao }
}
